/** @format */

import districts from './Districts'
import blocks from './Blocks'
import villages from './Villages'

// All available states sorted alphabetically
export const getAllStates = () => {
  return Object.keys(districts).sort((a, b) => a.localeCompare(b))
}

// Get districts list for a state
export const getDistrictsByState = stateName => {
  if (!stateName || typeof stateName !== 'string') return []
  const trimmed = stateName.trim()
  if (districts[trimmed]) {
    return districts[trimmed].map(d => d.name).filter(Boolean).sort()
  }
  // Case-insensitive fallback
  const foundKey = Object.keys(districts).find(
    k => k.toLowerCase() === trimmed.toLowerCase()
  )
  if (foundKey && districts[foundKey]) {
    return districts[foundKey].map(d => d.name).filter(Boolean).sort()
  }
  return []
}

// Get blocks list for a district
export const getBlocksByDistrict = districtName => {
  if (!districtName || typeof districtName !== 'string') return []
  const trimmed = districtName.trim()
  if (blocks[trimmed]) {
    return blocks[trimmed].map(b => b.name).filter(Boolean).sort()
  }
  // Case-insensitive fallback
  const foundKey = Object.keys(blocks).find(
    k => k.toLowerCase() === trimmed.toLowerCase()
  )
  if (foundKey && blocks[foundKey]) {
    return blocks[foundKey].map(b => b.name).filter(Boolean).sort()
  }
  return []
}

// Cache for ultra-fast village lookup by district and block
let villageCache = null

const getVillageCache = () => {
  if (!villageCache) {
    villageCache = new Map()
    for (let i = 0; i < villages.length; i++) {
      const v = villages[i]
      if (!v || !v.village) continue
      const d = (v.district || '').trim().toLowerCase()
      const b = (v.block || '').trim().toLowerCase()
      const key = `${d}:::${b}`
      if (!villageCache.has(key)) {
        villageCache.set(key, new Set())
      }
      villageCache.get(key).add(v.village.trim())
    }
  }
  return villageCache
}

// Get villages for selected district and block(s)
// blocksInput can be a string, array of strings, or array of react-select { value, label } objects
export const getVillagesByBlocks = (districtName, blocksInput) => {
  if (!districtName) return []
  const dTrimmed = (typeof districtName === 'string' ? districtName : '').trim().toLowerCase()
  if (!dTrimmed) return []

  const blockNames = []
  if (Array.isArray(blocksInput)) {
    blocksInput.forEach(b => {
      if (typeof b === 'string' && b.trim()) blockNames.push(b.trim().toLowerCase())
      else if (b && typeof b === 'object' && b.value) blockNames.push(String(b.value).trim().toLowerCase())
      else if (b && typeof b === 'object' && b.label) blockNames.push(String(b.label).trim().toLowerCase())
    })
  } else if (typeof blocksInput === 'string' && blocksInput.trim()) {
    blockNames.push(blocksInput.trim().toLowerCase())
  }

  const cache = getVillageCache()
  const result = new Set()

  if (blockNames.length === 0) {
    // If no block specified, return all villages for the district
    for (const [key, vSet] of cache.entries()) {
      if (key.startsWith(`${dTrimmed}:::`)) {
        vSet.forEach(v => result.add(v))
      }
    }
  } else {
    // Return villages matching the selected blocks
    blockNames.forEach(b => {
      const key = `${dTrimmed}:::${b}`
      const vSet = cache.get(key)
      if (vSet) {
        vSet.forEach(v => result.add(v))
      }
    })
  }

  return Array.from(result).sort((a, b) => a.localeCompare(b))
}

export { districts, blocks, villages }
export default {
  getAllStates,
  getDistrictsByState,
  getBlocksByDistrict,
  getVillagesByBlocks,
  districts,
  blocks,
  villages,
}
